import { Dialog, Portal } from '@chakra-ui/react';

type ResponsiveModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '4xl' | 'cover' | 'full'

// v3's stock dialog recipe shifts each named size step's maxW one token
// notch wider than Chakra v2's Modal used at the same step name (v2
// size="X" -> maxW token "X"; v3's own "X" step -> maxW one notch up). Four
// of ResponsiveModal's five actually-used sizes have an *exact* v3 step at
// the correct v2 width, just filed under a different v3 step name — reuse
// those directly, fully typed against Dialog.Root's own size union, no cast:
//   public 'sm'  -> v3 step 'xs' (both resolve to maxW token "sm",  24rem)
//   public 'md'  -> v3 step 'sm' (both resolve to maxW token "md",  28rem)
//   public 'lg'  -> v3 step 'md' (both resolve to maxW token "lg",  32rem)
//   public '4xl' -> v3 step 'xl' (both resolve to maxW token "4xl", 56rem)
//
// v2's Modal size="xl" (36rem) has no equivalent among v3's own dialog
// steps at all — v3 jumps straight from its "md" step (32rem) to "lg"
// (42rem), skipping 36rem entirely. theme.ts registers one small v2-compat
// step, "xlCompat", specifically to fill that one gap (spread from Chakra's
// own stock dialog recipe, not hand-rolled — see the comment there).
// Dialog.Root's generated `size` prop type is a closed literal union baked
// into @chakra-ui/react's shipped .d.ts files; it has no way to know about
// a custom recipe step added at runtime without also running
// `chakra-cli typegen` (which isn't durable here — see task-10-report.md).
// The single cast below (`as unknown as DialogRootSize`, only for the one
// step 'xl' actually needs) is the one narrow, intentional escape hatch
// this primitive needs to bridge that gap; every other size stays fully
// typed against Dialog.Root's real union, with no cast at all, and none of
// this is exposed to any of the ~380 consuming files.
type DialogRootSize = React.ComponentProps<typeof Dialog.Root>['size']

function toDialogSize(size: ResponsiveModalSize): DialogRootSize {
  switch (size) {
    case 'sm': return 'xs'
    case 'md': return 'sm'
    case 'lg': return 'md'
    case '4xl': return 'xl'
    case 'xl': return 'xlCompat' as unknown as DialogRootSize
    default: return size
  }
}

interface ResponsiveModalProps {
  isOpen: boolean
  onClose: () => void
  size?: ResponsiveModalSize
  isCentered?: boolean
  scrollBehavior?: 'inside' | 'outside'
  children: React.ReactNode
}

export function ResponsiveModal({
  isOpen, onClose, size = 'md', isCentered, scrollBehavior, children,
}: ResponsiveModalProps) {
  const dialogSize = toDialogSize(size)
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) onClose() }}
      size={{ base: 'full', md: dialogSize }}
      placement={isCentered ? 'center' : undefined}
      scrollBehavior={scrollBehavior}
    >
      <Portal>

        {children}

      </Portal>
    </Dialog.Root>
  );
}
